import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  documentId,
} from "firebase/firestore";
import { database } from "../../firebase";
import { serialize, deserialize } from "./utils";

const initialState = {
  byOrganization: {},
  lastVisibleAttendees: {},
  allAttendeesLoaded: {},
  loading: {},
  error: {},
  detail: {},
  detailLoading: {},
  /** eventCountByAttendeeId[organizationId][attendeeId] = eventCountTotal for ticket list */
  eventCountByAttendeeId: {},
  /** attendeeMetrics[organizationId][attendeeId] = { eventCountTotal, totalSpendUsdCents } for modal */
  attendeeMetrics: {},
};

const attendeesSlice = createSlice({
  name: "attendees",
  initialState,
  reducers: {
    clearAttendeesForOrganization: (state, action) => {
      const { organizationId } = action.payload;
      if (organizationId) {
        delete state.byOrganization[organizationId];
        delete state.lastVisibleAttendees[organizationId];
        delete state.allAttendeesLoaded[organizationId];
        delete state.loading[organizationId];
        delete state.error[organizationId];
      }
    },
    clearAttendeeDetail: (state, action) => {
      const { organizationId, attendeeId } = action.payload;
      if (organizationId && attendeeId) {
        delete state.detail[`${organizationId}/${attendeeId}`];
        delete state.detailLoading[`${organizationId}/${attendeeId}`];
      }
    },
    clearEventCountsForOrganization: (state, action) => {
      const { organizationId } = action.payload;
      if (organizationId && state.eventCountByAttendeeId) {
        delete state.eventCountByAttendeeId[organizationId];
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAttendees.fulfilled, (state, action) => {
        const {
          organizationId,
          attendees,
          lastVisible,
          allAttendeesLoaded,
          append,
        } = action.payload;
        if (append) {
          const existing = state.byOrganization[organizationId] || [];
          state.byOrganization[organizationId] = [...existing, ...attendees];
        } else {
          state.byOrganization[organizationId] = attendees;
        }
        state.lastVisibleAttendees[organizationId] = lastVisible;
        state.allAttendeesLoaded[organizationId] = allAttendeesLoaded;
        state.loading[organizationId] = false;
        state.error[organizationId] = null;
      })
      .addCase(fetchAttendees.pending, (state, { meta }) => {
        const { organizationId } = meta.arg;
        state.loading[organizationId] = true;
        state.error[organizationId] = null;
      })
      .addCase(fetchAttendees.rejected, (state, { meta, error }) => {
        const { organizationId } = meta.arg;
        state.loading[organizationId] = false;
        state.error[organizationId] = error?.message || "Failed to load attendees";
      })
      .addCase(fetchAttendeeDetail.fulfilled, (state, action) => {
        const { organizationId, attendeeId, attendee, tickets, eventsMap } =
          action.payload;
        const key = `${organizationId}/${attendeeId}`;
        state.detail[key] = { attendee, tickets, eventsMap: eventsMap || {} };
        state.detailLoading[key] = false;
      })
      .addCase(fetchAttendeeDetail.pending, (state, { meta }) => {
        const { organizationId, attendeeId } = meta.arg;
        state.detailLoading[`${organizationId}/${attendeeId}`] = true;
      })
      .addCase(fetchAttendeeDetail.rejected, (state, { meta }) => {
        const { organizationId, attendeeId } = meta.arg;
        state.detailLoading[`${organizationId}/${attendeeId}`] = false;
      })
      .addCase(fetchAttendeeEventCounts.fulfilled, (state, action) => {
        const { organizationId, eventCountByAttendeeId } = action.payload;
        if (!state.eventCountByAttendeeId[organizationId]) {
          state.eventCountByAttendeeId[organizationId] = {};
        }
        Object.assign(
          state.eventCountByAttendeeId[organizationId],
          eventCountByAttendeeId,
        );
      })
      .addCase(fetchAttendeeMetrics.fulfilled, (state, action) => {
        const { organizationId, attendeeId, eventCountTotal, totalSpendUsdCents } =
          action.payload;
        if (!state.attendeeMetrics[organizationId]) {
          state.attendeeMetrics[organizationId] = {};
        }
        state.attendeeMetrics[organizationId][attendeeId] = {
          eventCountTotal,
          totalSpendUsdCents,
        };
      });
  },
});

const SORT_FIELDS = [
  "lastSeenAt",
  "totalSpendUsdCents",
  "ticketCountAsPurchaser",
  "ticketCountAsAttendeeOnly",
  "orderCountAsPurchaser",
  "eventCountTotal",
  "checkedInCountTotal",
  "email",
  "displayName",
  "zipcode",
];

function deserializeSortKey(value, sortBy) {
  if (value == null) return null;
  const d = deserialize(value);
  if (d && typeof d.toDate === "function") return d.toDate();
  if (d && typeof d.seconds === "number") return new Date(d.seconds * 1000);
  return d;
}

export const fetchAttendees = createAsyncThunk(
  "attendees/fetchAttendees",
  async ({
    organizationId,
    pageSize = 25,
    lastVisible,
    sortBy = "lastSeenAt",
    sortOrder = "desc",
  }) => {
    const ref = collection(
      database,
      "organizations",
      organizationId,
      "attendees",
    );
    const orderDirection = sortOrder === "asc" ? "asc" : "desc";
    const field = SORT_FIELDS.includes(sortBy) ? sortBy : "lastSeenAt";

    let q = query(
      ref,
      orderBy(field, orderDirection),
      orderBy(documentId()),
      limit(pageSize),
    );
    if (lastVisible?.sortKey != null && lastVisible?.id) {
      const sortKeyVal = deserializeSortKey(lastVisible.sortKey, field);
      q = query(
        ref,
        orderBy(field, orderDirection),
        orderBy(documentId()),
        startAfter(sortKeyVal, lastVisible.id),
        limit(pageSize),
      );
    }
    const snapshot = await getDocs(q);
    const attendees = snapshot.docs.map((d) => ({
      id: d.id,
      ...deserialize(d.data()),
    }));
    let newLastVisible = null;
    if (snapshot.docs.length > 0) {
      const lastDoc = snapshot.docs[snapshot.docs.length - 1];
      const lastData = lastDoc.data();
      const rawKey = lastData[field];
      newLastVisible = {
        sortKey: serialize(rawKey !== undefined ? rawKey : null),
        id: lastDoc.id,
      };
    }
    const allAttendeesLoaded = snapshot.docs.length < pageSize;
    return {
      organizationId,
      attendees,
      lastVisible: newLastVisible,
      allAttendeesLoaded,
      append: !!lastVisible,
    };
  },
);

const FIRESTORE_IN_QUERY_LIMIT = 30;

/** Fetches eventCountTotal for given attendee IDs (by email). Used to show "Events attended" on ticket cards. */
export const fetchAttendeeEventCounts = createAsyncThunk(
  "attendees/fetchAttendeeEventCounts",
  async ({ organizationId, attendeeIds }) => {
    if (!organizationId || !attendeeIds?.length) {
      return { organizationId, eventCountByAttendeeId: {} };
    }
    const ref = collection(
      database,
      "organizations",
      organizationId,
      "attendees",
    );
    const eventCountByAttendeeId = {};
    const ids = [...new Set(attendeeIds)].filter(Boolean);
    for (let i = 0; i < ids.length; i += FIRESTORE_IN_QUERY_LIMIT) {
      const chunk = ids.slice(i, i + FIRESTORE_IN_QUERY_LIMIT);
      const q = query(ref, where(documentId(), "in", chunk));
      const snapshot = await getDocs(q);
      snapshot.docs.forEach((d) => {
        const data = d.data();
        const count = data?.eventCountTotal;
        eventCountByAttendeeId[d.id] =
          typeof count === "number" ? count : 0;
      });
    }
    return { organizationId, eventCountByAttendeeId };
  },
);

/** Fetches eventCountTotal and totalSpendUsdCents for one attendee (for ticket detail modal). */
export const fetchAttendeeMetrics = createAsyncThunk(
  "attendees/fetchAttendeeMetrics",
  async ({ organizationId, attendeeId }) => {
    const attendeeRef = doc(
      database,
      "organizations",
      organizationId,
      "attendees",
      attendeeId,
    );
    const snap = await getDoc(attendeeRef);
    const data = snap.exists() ? snap.data() : {};
    const eventCountTotal =
      typeof data.eventCountTotal === "number" ? data.eventCountTotal : 0;
    const totalSpendUsdCents =
      typeof data.totalSpendUsdCents === "number" ? data.totalSpendUsdCents : 0;
    return {
      organizationId,
      attendeeId,
      eventCountTotal,
      totalSpendUsdCents,
    };
  },
);

export const fetchAttendeeDetail = createAsyncThunk(
  "attendees/fetchAttendeeDetail",
  async ({ organizationId, attendeeId }) => {
    const attendeeRef = doc(
      database,
      "organizations",
      organizationId,
      "attendees",
      attendeeId,
    );
    const attendeeSnap = await getDoc(attendeeRef);
    if (!attendeeSnap.exists()) {
      throw new Error("Attendee not found");
    }
    const attendee = { id: attendeeSnap.id, ...deserialize(attendeeSnap.data()) };

    const ticketsRef = collection(
      database,
      "organizations",
      organizationId,
      "attendees",
      attendeeId,
      "tickets",
    );
    const ticketsQuery = query(
      ticketsRef,
      orderBy("createdAt", "desc"),
      limit(100),
    );
    const ticketsSnap = await getDocs(ticketsQuery);
    const tickets = ticketsSnap.docs.map((d) => ({
      id: d.id,
      ...deserialize(d.data()),
    }));

    const eventIds = [...new Set((tickets || []).map((t) => t.eventId).filter(Boolean))];
    const eventsMap = {};
    await Promise.all(
      eventIds.map(async (eventId) => {
        const eventSnap = await getDoc(doc(database, "events", eventId));
        if (eventSnap.exists()) {
          const data = eventSnap.data();
          eventsMap[eventId] = {
            name: data.name || eventId,
            ticketTypes: data.ticketTypes || [],
          };
        } else {
          eventsMap[eventId] = { name: eventId, ticketTypes: [] };
        }
      }),
    );

    return {
      organizationId,
      attendeeId,
      attendee,
      tickets,
      eventsMap: serialize(eventsMap),
    };
  },
);

export const {
  clearAttendeesForOrganization,
  clearAttendeeDetail,
  clearEventCountsForOrganization,
} = attendeesSlice.actions;

export default attendeesSlice.reducer;
